<template>
  <div class="scene-chart-menu" v-if="ctx.chart">
    <item-header
      :label="$t('objectNames.chart')"
      @close="$emit('close')"
    >
      <template #content>
        <editable-item-title :item="ctx.sceneNode" />
      </template>
    </item-header>

    <drawer-block :title="$t('labels.diagramConfig')" v-model:open="drawerStates.netChartAxes">
      <div class="column">
        <chart-type-chooser
          :chart-type="ctx.chart.chartType"
          @update:chartType="store.setChartType(ctx.chart.id, $event)"
        />
        <chart-axes-settings
          v-if="ctx.chart.chartType"
          :chart-type="ctx.chart.chartType"
          :axes="ctx.chart.chartSettings"
          @update:x-axis="store.setAxis(ctx.chart.id, 'x', $event)"
          @update:y-axis="store.setAxis(ctx.chart.id, 'y', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      :title="
        $t('objectNames.simulation', chartAxisConfig?.singleSimulation ? 1 : 10)
      "
      v-model:open="drawerStates.netChartSimulations"
    >
      <chart-builder-simulations :chart-uid="ctx.chart.uid" />
    </drawer-block>

    <!-- SINGLE ITEM -->
    <drawer-block
      v-if="chartAxisConfig?.singleItem"
      :title="$t('labels.data')"
      v-model:open="drawerStates.netChartData"
    >
      <chart-builder-item-single
        :chart-uid="ctx.chart.uid"
        :item-options="itemOptions"
        :selected-item="selectedItem"
        @selectItem="selectItem"
      />
    </drawer-block>

    <drawer-block
      v-else-if="ctx.chart.chartType"
      :title="$t('labels.data')"
      v-model:open="drawerStates.netChartData"
    >
      <chart-builder-items :chart-uid="ctx.chart.uid" />
    </drawer-block>

    <drawer-block :title="$t('labels.design')" v-model:open="drawerStates.netChartDesign">
      <chart-settings-list
        :chart="ctx.chart"
        :settings="settings"
        :remove-settings="['width', 'height']"
        @update="updateChartSettings($event)"
      ></chart-settings-list>
    </drawer-block>

    <!-- DELETE -->
    <menu-group class="mt-auto">
      <button
        type="button"
        class="button button_secondary mv-2"
        @click="ctx.delete()"
      >
        {{ $t("actions.deleteChart", 1) }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";

import ChartTypeChooser from "@editor/items/charts/ChartTypeChooser.vue";
import ChartAxesSettings from "@editor/items/charts/ChartAxesSettings.vue";
import ChartSettingsList from "@editor/items/charts/ChartSettingsList.vue";
import ChartBuilderSimulations from "@editor/items/charts/ChartBuilderSimulations.vue";
import ChartBuilderItems from "@editor/items/charts/ChartBuilderItems.vue";
import ChartBuilderItemSingle from "@editor/items/charts/ChartBuilderItemSingle.vue";

import { useSceneChart } from "./useSceneChart";
import { useChartData } from "../charts/useChartData";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store"

import type {
  ChartSettings,
  SelectableChartItem
} from "@editor/items/charts/ChartItem";
import type { Uid } from "@/helpers/utility/Id";
import { useChartStore } from "../charts/charts.store";

const props = defineProps<{
  nodeUid: Uid<"sceneNode">;
}>();
const emit = defineEmits<{
  (e: "close"): void;
}>();

const ctx = useSceneChart(() => props.nodeUid);
const drawerStates = useDrawerStates();
const store = useChartStore();

const settings = computed(() => ({
  ...ctx.chart?.chartSettings
}));

const { chartAxisConfig, itemOptions, selectedItem } = useChartData(() => ctx.chart);

function selectItem(item: SelectableChartItem | null) {
  if (!ctx.chart) return;
  store.selectItem(ctx.chart.id, item);
}

function updateChartSettings(settings: Partial<ChartSettings>) {
  if (!ctx.chart) return;
  return store.updateChartSettings(ctx.chart.id, settings);
}
</script>

<style lang="scss">
.scene-chart-menu {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
